/**
 * Evidence tab (FR-3.1–3.6, DC-1793 demo D3) — the attestation-first work
 * surface. In-scope tests grouped by evidence channel, each showing what
 * would answer it (the catalog's evidence description) and what currently
 * does. Honesty rules carried into the UI:
 *
 *  - a test with no readable channel renders under "Unrouted", stated —
 *    never silently omitted (FR-3.1 AC fail);
 *  - a manual metric entry shows a PREVIEW band — copy says preview; only a
 *    run computes findings;
 *  - an unparsed-spec test states that its evaluation is documented, not
 *    yet wired, and offers attestation instead of a value field;
 *  - unconfirmed interview candidates are labelled as candidates — not
 *    evidence until a human confirms;
 *  - an expired or not-relied-upon artefact says so, with the reason;
 *  - a document with no confirmed link is visible in the locker and
 *    honestly satisfies nothing.
 *
 * ARC IP boundary: identifiers + entitled catalog content only — everything
 * shown arrives from the API at runtime; nothing is embedded here.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import { ActorPicker } from './ActorPicker';
import { LINE_LABEL, type AttributedActor } from './assessmentTypes';

// ── Payload types (mirror server/banking/assessment/evidenceRepository) ──

type EvidenceLeg = 'data' | 'document' | 'system' | 'interview' | 'external';

interface SpecSummary {
  bandForm: 'three_band_numeric' | 'two_band_numeric' | 'categorical' | 'unparsed';
  metricInput: string | null;
  bounds: Record<string, string> | null;
  scores: { pass: number; amber: number | null; fail: number } | null;
}

interface SpecPreview {
  band: 'pass' | 'amber' | 'fail';
  score: number;
  metricInput: string;
}

interface ManualEntrySummary {
  id: string;
  metricInput: string;
  metricValue: number;
  enteredByName: string;
  createdAt: string;
  preview: SpecPreview | null;
  /** 3LOD (DC-1805): whose number this is; absent = "line unstated". */
  suppliedBy?: AttributedActor;
}

interface EvidenceItemSummary {
  linkId: string;
  itemId: string;
  kind:
    | 'document'
    | 'dataset_ref'
    | 'system_attestation'
    | 'interview_attestation'
    | 'external_artefact';
  label: string;
  sourceRef: string | null;
  interview?: {
    speaker: string;
    stance: 'attests' | 'contradicts';
    quote: string;
    confirmed: boolean;
  };
  external?: {
    artefactKind: string;
    issuer: string;
    asOfDate: string;
    expiresAt: string;
    expired: boolean;
    reliability: 'relied_upon' | 'not_relied_upon';
    reliabilityReason: string | null;
    /** DC-1808: stated independence, welded to its note. */
    sourceIndependence?: 'independent' | 'non_independent' | 'unstated';
    independenceNote?: string | null;
  };
  system?: {
    systemName: string;
    systemClass: string;
    attributesAsserted: string[];
    attestedAt: string;
  };
  verification?: VerificationSummary;
  /** 3LOD (DC-1805): the attributed actor; absent = "line unstated". */
  attributedActor?: AttributedActor;
}

/** Latest document-verifier report for one (document × test) — D6, FR-6.1.
 * A verdict with citations, structurally score-free. */
interface VerificationSummary {
  verificationId: string;
  testCode: string;
  verdict: 'present' | 'present_with_gaps' | 'absent';
  artefactRecognized: boolean;
  discardedUncited: number;
  verifierId: string;
  verifierVersion: string;
  verifiedAt: string;
  provisions: {
    key: string;
    label: string;
    status: 'evidenced' | 'gap';
    quote: string | null;
    locator: string | null;
  }[];
}

const VERDICT_COPY: Record<VerificationSummary['verdict'], string> = {
  present: 'present — every provision cited',
  present_with_gaps: 'present with gaps',
  absent: 'absent — no citable support',
};

interface EvidenceTest {
  testCode: string;
  controlCode: string;
  leg: EvidenceLeg | null;
  /** Catalog name beside the identifier (DC-1807); null = code stands alone. */
  testName: string | null;
  evidenceText: string | null;
  spec: SpecSummary | null;
  manualEntries: ManualEntrySummary[];
  evidence: EvidenceItemSummary[];
}

interface LockerDocument {
  itemId: string;
  label: string;
  sourceRef: string | null;
  linkedTests: string[];
  verifications: VerificationSummary[];
}

interface EvidenceWorkspace {
  catalogVersion: string | null;
  tests: EvidenceTest[];
  documents: LockerDocument[];
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'ready'; workspace: EvidenceWorkspace };

const LEG_ORDER: (EvidenceLeg | 'unrouted')[] = [
  'data',
  'document',
  'system',
  'interview',
  'external',
  'unrouted',
];

const LEG_COPY: Record<EvidenceLeg | 'unrouted', { title: string; note: string }> = {
  data: { title: 'Data', note: 'Answered by measured values — supplied now, collected by Compass as collectors arrive.' },
  document: { title: 'Documents', note: 'Answered by governance artefacts in the evidence locker, linked by a human.' },
  system: { title: 'Systems', note: 'Answered by structured attestations about the systems of record.' },
  interview: { title: 'Interviews', note: 'Answered last, by confirmed verbatim statements — never a filter for other legs.' },
  external: { title: 'External', note: 'Answered by third-party artefacts — 12-month horizon, reliability switch.' },
  unrouted: { title: 'Unrouted', note: 'These in-scope tests carry no evidence-channel assignment yet. They are listed — never silently omitted — and resolve as not assessable until routed.' },
};

const SEEDED_SYSTEM_ATTRIBUTES = ['completeness', 'ownership', 'access_limitation'] as const;

/**
 * useApiClient surfaces the server's error.message on thrown errors — the
 * typed refusals (409 EVALUATION_SPEC_UNPARSED etc.) carry their reason
 * there. Bare transport failures fall back to honest generic copy.
 */
function refusalMessage(error: unknown, fallback: string): string {
  const message = error instanceof Error ? error.message : '';
  return message && !/^HTTP \d+$/.test(message) ? message : fallback;
}

// ── Per-test entry forms ─────────────────────────────────────────────────

const MetricEntryForm: React.FC<{
  useCaseId: string;
  test: EvidenceTest;
  onRecorded: () => void;
}> = ({ useCaseId, test, onRecorded }) => {
  const { post } = useApiClient();
  const [value, setValue] = useState('');
  const [actorId, setActorId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = useCallback(async () => {
    const numeric = Number(value);
    if (value.trim() === '' || !Number.isFinite(numeric)) {
      setMessage('Enter a numeric value.');
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await post(`/api/use-cases/${useCaseId}/evidence/metric-entries`, {
        testCode: test.testCode,
        value: numeric,
        ...(actorId ? { suppliedByActorId: actorId } : {}),
      });
      setValue('');
      onRecorded();
    } catch (error) {
      setMessage(refusalMessage(error, 'The entry could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }, [post, useCaseId, test.testCode, value, actorId, onRecorded]);

  const inputName = test.spec?.metricInput ?? 'value';
  return (
    <div className="uc-evidence-form" data-testid={`uc-evidence-metric-form-${test.testCode}`}>
      <label className="uc-evidence-form-label">
        Supply {inputName}
        <input
          type="number"
          step="any"
          value={value}
          disabled={busy}
          onChange={(e) => setValue(e.target.value)}
          aria-label={`Value for ${inputName} on ${test.testCode}`}
        />
      </label>
      <ActorPicker
        idPrefix={`metric-${test.testCode}`}
        label="Whose number this is"
        value={actorId}
        onChange={setActorId}
        disabled={busy}
      />
      <button
        type="button"
        className="banking-btn banking-btn-primary"
        disabled={busy || value.trim() === ''}
        onClick={() => void submit()}
      >
        Record value
      </button>
      {message && (
        <p className="uc-evidence-refusal" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

const InterviewForm: React.FC<{
  useCaseId: string;
  testCode: string;
  onRecorded: () => void;
}> = ({ useCaseId, testCode, onRecorded }) => {
  const { post } = useApiClient();
  const [speaker, setSpeaker] = useState('');
  const [quote, setQuote] = useState('');
  const [stance, setStance] = useState<'attests' | 'contradicts'>('attests');
  const [actorId, setActorId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      await post(`/api/use-cases/${useCaseId}/evidence/interview-attestations`, {
        testCode,
        speaker: speaker.trim(),
        quote: quote.trim(),
        stance,
        ...(actorId ? { attestedByActorId: actorId } : {}),
      });
      setSpeaker('');
      setQuote('');
      onRecorded();
    } catch (error) {
      setMessage(refusalMessage(error, 'The attestation could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }, [post, useCaseId, testCode, speaker, quote, stance, actorId, onRecorded]);

  return (
    <div className="uc-evidence-form" data-testid={`uc-evidence-interview-form-${testCode}`}>
      <label className="uc-evidence-form-label">
        Speaker
        <input value={speaker} disabled={busy} onChange={(e) => setSpeaker(e.target.value)} />
      </label>
      <label className="uc-evidence-form-label uc-evidence-form-wide">
        Verbatim quote
        <textarea
          value={quote}
          disabled={busy}
          rows={2}
          onChange={(e) => setQuote(e.target.value)}
          placeholder="Verbatim, or it is not evidence."
        />
      </label>
      <label className="uc-evidence-form-label">
        Stance
        <select
          value={stance}
          disabled={busy}
          onChange={(e) => setStance(e.target.value as 'attests' | 'contradicts')}
        >
          <option value="attests">Attests</option>
          <option value="contradicts">Contradicts</option>
        </select>
      </label>
      <ActorPicker
        idPrefix={`interview-${testCode}`}
        label="Speaker as a registered actor"
        value={actorId}
        onChange={setActorId}
        disabled={busy}
      />
      <button
        type="button"
        className="banking-btn banking-btn-primary"
        disabled={busy || speaker.trim() === '' || quote.trim() === ''}
        onClick={() => void submit()}
      >
        Record candidate
      </button>
      {message && (
        <p className="uc-evidence-refusal" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

const ExternalForm: React.FC<{
  useCaseId: string;
  testCode: string;
  onRecorded: () => void;
}> = ({ useCaseId, testCode, onRecorded }) => {
  const { post } = useApiClient();
  const [kind, setKind] = useState('audit');
  const [issuer, setIssuer] = useState('');
  const [asOfDate, setAsOfDate] = useState('');
  const [actorId, setActorId] = useState<string | null>(null);
  const [independence, setIndependence] = useState<'independent' | 'non_independent' | 'unstated'>(
    'unstated'
  );
  const [independenceNote, setIndependenceNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const submit = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      await post(`/api/use-cases/${useCaseId}/evidence/external-artefacts`, {
        kind,
        issuer: issuer.trim(),
        asOfDate,
        testCodes: [testCode],
        ...(actorId ? { issuedByActorId: actorId } : {}),
        sourceIndependence: independence,
        ...(independence !== 'unstated' ? { independenceNote: independenceNote.trim() } : {}),
      });
      setIssuer('');
      setAsOfDate('');
      setIndependence('unstated');
      setIndependenceNote('');
      onRecorded();
    } catch (error) {
      setMessage(refusalMessage(error, 'The artefact could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }, [
    post,
    useCaseId,
    testCode,
    kind,
    issuer,
    asOfDate,
    actorId,
    independence,
    independenceNote,
    onRecorded,
  ]);

  // DC-1808 weld, stated at the form: a claim needs its basis; a known
  // non-independence needs the named relationship.
  const noteMissing = independence !== 'unstated' && independenceNote.trim() === '';

  return (
    <div className="uc-evidence-form" data-testid={`uc-evidence-external-form-${testCode}`}>
      <label className="uc-evidence-form-label">
        Kind
        <select value={kind} disabled={busy} onChange={(e) => setKind(e.target.value)}>
          <option value="audit">Audit</option>
          <option value="independent_validation">Independent validation</option>
          <option value="regulator_exam">Regulator exam</option>
        </select>
      </label>
      <label className="uc-evidence-form-label">
        Issuer
        <input value={issuer} disabled={busy} onChange={(e) => setIssuer(e.target.value)} />
      </label>
      <label className="uc-evidence-form-label">
        As of
        <input
          type="date"
          value={asOfDate}
          disabled={busy}
          onChange={(e) => setAsOfDate(e.target.value)}
        />
      </label>
      <ActorPicker
        idPrefix={`external-${testCode}`}
        label="Issuing party as a registered actor"
        value={actorId}
        onChange={setActorId}
        disabled={busy}
      />
      <label className="uc-evidence-form-label">
        Source independence
        <select
          value={independence}
          disabled={busy}
          data-testid={`uc-evidence-independence-${testCode}`}
          onChange={(e) =>
            setIndependence(e.target.value as 'independent' | 'non_independent' | 'unstated')
          }
        >
          <option value="unstated">Unstated</option>
          <option value="independent">Independent of the process it reviews</option>
          <option value="non_independent">Not independent — engaged by the process</option>
        </select>
      </label>
      {independence !== 'unstated' && (
        <label className="uc-evidence-form-label uc-evidence-form-wide">
          {independence === 'independent'
            ? 'Basis for the independence claim'
            : 'The relationship (e.g. consultants engaged by the front office)'}
          <textarea
            value={independenceNote}
            disabled={busy}
            rows={2}
            onChange={(e) => setIndependenceNote(e.target.value)}
          />
        </label>
      )}
      <button
        type="button"
        className="banking-btn banking-btn-primary"
        disabled={busy || issuer.trim() === '' || asOfDate === '' || noteMissing}
        onClick={() => void submit()}
      >
        Record artefact
      </button>
      <p className="uc-evidence-form-hint">Expires 12 months after the as-of date — an older artefact stops satisfying, visibly.</p>
      {noteMissing && (
        <p className="uc-evidence-form-hint">
          A stated independence carries its note: the basis for the claim, or the named
          relationship. Unstated carries neither — and renders as unstated.
        </p>
      )}
      {message && (
        <p className="uc-evidence-refusal" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

const SystemForm: React.FC<{
  useCaseId: string;
  testCode: string;
  onRecorded: () => void;
}> = ({ useCaseId, testCode, onRecorded }) => {
  const { post } = useApiClient();
  const [systemName, setSystemName] = useState('');
  const [systemClass, setSystemClass] = useState('');
  const [attributes, setAttributes] = useState<string[]>([]);
  const [actorId, setActorId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const toggle = (attribute: string): void => {
    setAttributes((prev) =>
      prev.includes(attribute) ? prev.filter((a) => a !== attribute) : [...prev, attribute]
    );
  };

  const submit = useCallback(async () => {
    setBusy(true);
    setMessage(null);
    try {
      await post(`/api/use-cases/${useCaseId}/evidence/system-attestations`, {
        systemName: systemName.trim(),
        systemClass: systemClass.trim(),
        attributesAsserted: attributes,
        testCodes: [testCode],
        ...(actorId ? { attestedByActorId: actorId } : {}),
      });
      setSystemName('');
      setSystemClass('');
      setAttributes([]);
      onRecorded();
    } catch (error) {
      setMessage(refusalMessage(error, 'The attestation could not be recorded.'));
    } finally {
      setBusy(false);
    }
  }, [post, useCaseId, testCode, systemName, systemClass, attributes, actorId, onRecorded]);

  return (
    <div className="uc-evidence-form" data-testid={`uc-evidence-system-form-${testCode}`}>
      <label className="uc-evidence-form-label">
        System name
        <input value={systemName} disabled={busy} onChange={(e) => setSystemName(e.target.value)} />
      </label>
      <label className="uc-evidence-form-label">
        System class
        <input
          value={systemClass}
          disabled={busy}
          onChange={(e) => setSystemClass(e.target.value)}
          placeholder="e.g. inventory, repository"
        />
      </label>
      <fieldset className="uc-evidence-attributes">
        <legend>Attributes asserted</legend>
        {SEEDED_SYSTEM_ATTRIBUTES.map((attribute) => (
          <label key={attribute} className="uc-evidence-attribute">
            <input
              type="checkbox"
              checked={attributes.includes(attribute)}
              disabled={busy}
              onChange={() => toggle(attribute)}
            />
            {attribute.replace(/_/g, ' ')}
          </label>
        ))}
      </fieldset>
      <ActorPicker
        idPrefix={`system-${testCode}`}
        label="Whose attestation this is"
        value={actorId}
        onChange={setActorId}
        disabled={busy}
      />
      <button
        type="button"
        className="banking-btn banking-btn-primary"
        disabled={busy || systemName.trim() === '' || systemClass.trim() === '' || attributes.length === 0}
        onClick={() => void submit()}
      >
        Record attestation
      </button>
      <p className="uc-evidence-form-hint">An attestation that asserts nothing attests nothing — pick at least one attribute.</p>
      {message && (
        <p className="uc-evidence-refusal" role="alert">
          {message}
        </p>
      )}
    </div>
  );
};

// ── The tab ──────────────────────────────────────────────────────────────

export const UseCaseEvidence: React.FC<{ useCaseId: string }> = ({ useCaseId }) => {
  const { get, post, delete: del, authFetch } = useApiClient();
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;

  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [openForm, setOpenForm] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [linkChoice, setLinkChoice] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const res = await get<{ data?: { workspace?: EvidenceWorkspace } }>(
        `/api/use-cases/${useCaseId}/evidence`
      );
      const workspace = res.data?.workspace;
      if (!workspace) {
        setState({ phase: 'error' });
        return;
      }
      setState({ phase: 'ready', workspace });
    } catch {
      setState({ phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const recorded = useCallback(() => {
    setOpenForm(null);
    void load();
  }, [load]);

  const confirmAttestation = useCallback(
    async (attestationLinkItemId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await post(
          `/api/use-cases/${useCaseId}/evidence/interview-attestations/${attestationLinkItemId}/confirm`,
          {}
        );
        await load();
      } catch (error) {
        setActionError(refusalMessage(error, 'The attestation could not be confirmed.'));
      } finally {
        setBusy(false);
      }
    },
    [post, useCaseId, load]
  );

  const unlink = useCallback(
    async (linkId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await del(`/api/use-cases/${useCaseId}/evidence/links/${linkId}`);
        await load();
      } catch (error) {
        setActionError(refusalMessage(error, 'The link could not be removed.'));
      } finally {
        setBusy(false);
      }
    },
    [del, useCaseId, load]
  );

  const uploadDocument = useCallback(
    async (file: File) => {
      setBusy(true);
      setActionError(null);
      try {
        const form = new FormData();
        form.append('file', file);
        const res = await authFetch(`/api/use-cases/${useCaseId}/evidence/documents`, {
          method: 'POST',
          body: form,
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as {
            error?: { message?: string };
          } | null;
          setActionError(body?.error?.message ?? 'The document could not be uploaded.');
          return;
        }
        await load();
      } catch {
        setActionError('The document could not be uploaded.');
      } finally {
        setBusy(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [authFetch, useCaseId, load]
  );

  const verifyDocument = useCallback(
    async (itemId: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await post(`/api/use-cases/${useCaseId}/evidence/documents/${itemId}/verify`, {});
        await load();
      } catch (error) {
        setActionError(
          refusalMessage(error, 'The document could not be verified. Its links are unchanged.')
        );
      } finally {
        setBusy(false);
      }
    },
    [post, useCaseId, load]
  );

  const linkDocument = useCallback(
    async (itemId: string, testCode: string) => {
      setBusy(true);
      setActionError(null);
      try {
        await post(`/api/use-cases/${useCaseId}/evidence/items/${itemId}/links`, { testCode });
        setLinkChoice((prev) => ({ ...prev, [itemId]: '' }));
        await load();
      } catch (error) {
        setActionError(refusalMessage(error, 'The link could not be recorded.'));
      } finally {
        setBusy(false);
      }
    },
    [post, useCaseId, load]
  );

  if (state.phase === 'loading') {
    return (
      <section className="uc-evidence" aria-label="Evidence" aria-busy="true">
        <p className="uc-evidence-note">Reading the evidence workspace…</p>
      </section>
    );
  }

  if (state.phase === 'error') {
    return (
      <section className="uc-evidence" aria-label="Evidence">
        <p className="uc-evidence-note" role="alert" data-testid="uc-evidence-error">
          The evidence workspace could not be read.
        </p>
        <button
          type="button"
          className="banking-btn banking-btn-secondary"
          onClick={() => void load()}
        >
          Retry
        </button>
      </section>
    );
  }

  const { workspace } = state;
  const groups = LEG_ORDER.map((leg) => ({
    leg,
    tests: workspace.tests.filter((t) => (t.leg ?? 'unrouted') === leg),
  })).filter((g) => g.tests.length > 0);

  return (
    <section className="uc-evidence" aria-label="Evidence" data-testid="uc-evidence">
      <header className="uc-evidence-header">
        <p className="uc-evidence-lede">
          {workspace.tests.length === 0
            ? 'No tests are in scope yet. Build and validate the ARC scope first — evidence collects against in-scope tests.'
            : `${workspace.tests.length} in-scope test${workspace.tests.length === 1 ? '' : 's'}, grouped by the evidence channel that answers them. Entries and attestations persist here; findings are computed only when an assessment runs.`}
        </p>
        {workspace.catalogVersion && (
          <p className="uc-evidence-catalog">Catalog {workspace.catalogVersion}</p>
        )}
      </header>

      {actionError && (
        <p className="uc-evidence-refusal" role="alert" data-testid="uc-evidence-action-error">
          {actionError}
        </p>
      )}

      {/* Evidence locker — documents live at use-case level, link to many tests. */}
      <div className="uc-evidence-locker" data-testid="uc-evidence-locker">
        <h3>Evidence locker</h3>
        {workspace.documents.length === 0 ? (
          <p className="uc-evidence-note">No documents uploaded yet.</p>
        ) : (
          <ul className="uc-evidence-locker-list">
            {workspace.documents.map((doc) => (
              <li key={doc.itemId} className="uc-evidence-locker-row">
                <span className="uc-evidence-doc-label">{doc.label}</span>
                {doc.linkedTests.length === 0 ? (
                  <span className="uc-evidence-doc-unlinked" data-testid={`uc-evidence-doc-unlinked-${doc.itemId}`}>
                    Not linked to any test — satisfies nothing until a link is confirmed.
                  </span>
                ) : (
                  <span className="uc-evidence-doc-links">
                    Linked: {doc.linkedTests.join(', ')}
                  </span>
                )}
                {doc.linkedTests.length > 0 && (
                  <span className="uc-evidence-doc-verifications" data-testid={`uc-evidence-doc-verifications-${doc.itemId}`}>
                    {doc.linkedTests.map((testCode) => {
                      // Typed field only; an older payload without the field
                      // reads as unverified, never as a crash.
                      const verification = (doc.verifications ?? []).find(
                        (v) => v.testCode === testCode
                      );
                      return (
                        <span
                          key={testCode}
                          className={`uc-evidence-verdict uc-evidence-verdict-${verification?.verdict ?? 'unverified'}`}
                        >
                          {testCode}:{' '}
                          {verification ? VERDICT_COPY[verification.verdict] : 'unverified'}
                        </span>
                      );
                    })}
                  </span>
                )}
                {canEdit && doc.linkedTests.length > 0 && (
                  <button
                    type="button"
                    className="banking-btn banking-btn-secondary"
                    disabled={busy}
                    data-testid={`uc-evidence-verify-${doc.itemId}`}
                    onClick={() => void verifyDocument(doc.itemId)}
                  >
                    Verify against linked tests
                  </button>
                )}
                {canEdit && (
                  <span className="uc-evidence-doc-linker">
                    <select
                      aria-label={`Link ${doc.label} to a test`}
                      value={linkChoice[doc.itemId] ?? ''}
                      disabled={busy}
                      onChange={(e) =>
                        setLinkChoice((prev) => ({ ...prev, [doc.itemId]: e.target.value }))
                      }
                    >
                      <option value="">Link to test…</option>
                      {workspace.tests
                        .filter((t) => !doc.linkedTests.includes(t.testCode))
                        .map((t) => (
                          <option key={t.testCode} value={t.testCode}>
                            {/* DC-1807: the picker names the test, not just the code. */}
                            {t.testName ? `${t.testCode} — ${t.testName}` : t.testCode}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="banking-btn banking-btn-secondary"
                      disabled={busy || !(linkChoice[doc.itemId] ?? '')}
                      onClick={() => void linkDocument(doc.itemId, linkChoice[doc.itemId])}
                    >
                      Confirm link
                    </button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {canEdit && (
          <label className="uc-evidence-upload">
            Upload document
            <input
              ref={fileInputRef}
              type="file"
              disabled={busy}
              data-testid="uc-evidence-upload"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void uploadDocument(file);
              }}
            />
          </label>
        )}
      </div>

      {groups.map(({ leg, tests }) => (
        <div key={leg} className={`uc-evidence-group uc-evidence-group-${leg}`} data-testid={`uc-evidence-group-${leg}`}>
          <h3 className="uc-evidence-group-title">
            {LEG_COPY[leg].title}
            <span className="uc-evidence-group-count">{tests.length}</span>
          </h3>
          <p className="uc-evidence-group-note">{LEG_COPY[leg].note}</p>
          <ul className="uc-evidence-test-list">
            {tests.map((test) => {
              const specParsed =
                test.spec !== null &&
                (test.spec.bandForm === 'three_band_numeric' ||
                  test.spec.bandForm === 'two_band_numeric');
              return (
                <li key={test.testCode} className="uc-evidence-test" data-testid={`uc-evidence-test-${test.testCode}`}>
                  <div className="uc-evidence-test-head">
                    <span className="uc-evidence-test-code">{test.testCode}</span>
                    {/* DC-1807: name beside identifier, entitlement-served;
                        absent when the catalog is unreadable — never faked. */}
                    {test.testName && (
                      <span
                        className="uc-evidence-test-name"
                        data-testid={`uc-evidence-test-name-${test.testCode}`}
                      >
                        {test.testName}
                      </span>
                    )}
                    <span className="uc-evidence-test-control">{test.controlCode}</span>
                  </div>
                  <p className="uc-evidence-test-text">
                    {test.evidenceText ?? 'The entitled catalog carries no evidence description for this test.'}
                  </p>
                  {test.spec?.bandForm === 'unparsed' && (
                    <p className="uc-evidence-test-unparsed">
                      Evaluation documented, not yet wired — a supplied value cannot band here.
                      Record an attestation; a run will state attestation required.
                    </p>
                  )}

                  {(test.manualEntries.length > 0 || test.evidence.length > 0) && (
                    <ul className="uc-evidence-current">
                      {test.manualEntries.map((entry) => (
                        <li key={entry.id} className="uc-evidence-entry">
                          <span className="uc-evidence-entry-value">
                            {entry.metricInput} = {entry.metricValue}
                          </span>
                          {entry.preview ? (
                            <span className={`uc-evidence-band uc-evidence-band-${entry.preview.band}`}>
                              preview {entry.preview.band} / {entry.preview.score}
                            </span>
                          ) : (
                            <span className="uc-evidence-band uc-evidence-band-none">no preview</span>
                          )}
                          <span className="uc-evidence-provenance">
                            {entry.enteredByName} · {new Date(entry.createdAt).toLocaleDateString()}
                          </span>
                          {/* 3LOD: whose number — or the honest absence. */}
                          {entry.suppliedBy ? (
                            <span
                              className="uc-evidence-actor"
                              data-testid={`uc-evidence-entry-actor-${entry.id}`}
                            >
                              supplied by {entry.suppliedBy.displayName} —{' '}
                              {entry.suppliedBy.roleTitle} · {LINE_LABEL[entry.suppliedBy.line]}
                            </span>
                          ) : (
                            <span className="uc-evidence-actor uc-evidence-actor-unstated">
                              line unstated
                            </span>
                          )}
                        </li>
                      ))}
                      {test.evidence.map((item) => (
                        <li key={item.linkId} className="uc-evidence-item">
                          <span className="uc-evidence-item-label">{item.label}</span>
                          {item.interview && (
                            <span className="uc-evidence-item-detail">
                              “{item.interview.quote}” — {item.interview.speaker} ·{' '}
                              {item.interview.stance}
                              {item.interview.confirmed ? (
                                <span className="uc-evidence-confirmed"> · confirmed</span>
                              ) : (
                                <span className="uc-evidence-candidate" data-testid={`uc-evidence-candidate-${item.itemId}`}>
                                  {' '}
                                  · candidate — not evidence until confirmed
                                  {canEdit && (
                                    <button
                                      type="button"
                                      className="banking-btn banking-btn-secondary"
                                      disabled={busy}
                                      onClick={() => void confirmAttestation(item.itemId)}
                                    >
                                      Confirm
                                    </button>
                                  )}
                                </span>
                              )}
                            </span>
                          )}
                          {item.external && (
                            <span className="uc-evidence-item-detail">
                              {item.external.issuer} · as of {item.external.asOfDate} · expires{' '}
                              {item.external.expiresAt}
                              {item.external.expired && (
                                <span className="uc-evidence-expired" data-testid={`uc-evidence-expired-${item.itemId}`}>
                                  {' '}
                                  · expired — no longer satisfies
                                </span>
                              )}
                              {item.external.reliability === 'not_relied_upon' && (
                                <span className="uc-evidence-expired">
                                  {' '}
                                  · not relied upon: {item.external.reliabilityReason}
                                </span>
                              )}
                              {/* DC-1808: independence stated beside reliability —
                                  orthogonal, both visible, unstated says so. */}
                              <span
                                className={`uc-evidence-independence uc-evidence-independence-${item.external.sourceIndependence ?? 'unstated'}`}
                                data-testid={`uc-evidence-independence-state-${item.itemId}`}
                              >
                                {' '}
                                ·{' '}
                                {(item.external.sourceIndependence ?? 'unstated') === 'independent'
                                  ? `independent — ${item.external.independenceNote}`
                                  : (item.external.sourceIndependence ?? 'unstated') ===
                                      'non_independent'
                                    ? `not independent — ${item.external.independenceNote}`
                                    : 'independence unstated'}
                              </span>
                            </span>
                          )}
                          {item.system && (
                            <span className="uc-evidence-item-detail">
                              {item.system.systemName} ({item.system.systemClass}) ·{' '}
                              {item.system.attributesAsserted.join(', ')}
                            </span>
                          )}
                          {/* 3LOD: the attributed actor on attestation satellites —
                              or the honest absence. Documents carry no attribution
                              in this slice (stage-2/3 ownership). */}
                          {item.kind !== 'document' &&
                            item.kind !== 'dataset_ref' &&
                            (item.attributedActor ? (
                              <span
                                className="uc-evidence-actor"
                                data-testid={`uc-evidence-item-actor-${item.itemId}`}
                              >
                                {item.kind === 'external_artefact' ? 'issued by' : 'attested by'}{' '}
                                {item.attributedActor.displayName} —{' '}
                                {item.attributedActor.roleTitle} ·{' '}
                                {LINE_LABEL[item.attributedActor.line]}
                              </span>
                            ) : (
                              <span className="uc-evidence-actor uc-evidence-actor-unstated">
                                line unstated
                              </span>
                            ))}
                          {item.kind === 'document' &&
                            (item.verification ? (
                              <details
                                className="uc-evidence-verification"
                                data-testid={`uc-evidence-verification-${item.itemId}-${test.testCode}`}
                              >
                                <summary>
                                  <span
                                    className={`uc-evidence-verdict uc-evidence-verdict-${item.verification.verdict}`}
                                  >
                                    Verified: {VERDICT_COPY[item.verification.verdict]}
                                  </span>
                                  {!item.verification.artefactRecognized && (
                                    <span className="uc-evidence-expired">
                                      {' '}
                                      · not the artefact this test names
                                    </span>
                                  )}
                                  {item.verification.discardedUncited > 0 && (
                                    <span className="uc-evidence-expired">
                                      {' '}
                                      · {item.verification.discardedUncited} uncited claim
                                      {item.verification.discardedUncited === 1 ? '' : 's'} discarded
                                    </span>
                                  )}
                                </summary>
                                <ul className="uc-evidence-provision-list">
                                  {item.verification.provisions.map((provision) => (
                                    <li
                                      key={provision.key}
                                      className={`uc-evidence-provision uc-evidence-provision-${provision.status}`}
                                    >
                                      <span className="uc-evidence-provision-label">
                                        {provision.label}
                                      </span>
                                      {provision.status === 'evidenced' ? (
                                        <span className="uc-evidence-provision-citation">
                                          “{provision.quote}” — {provision.locator}
                                        </span>
                                      ) : (
                                        <span className="uc-evidence-provision-gap">
                                          gap — not stated in this document
                                        </span>
                                      )}
                                    </li>
                                  ))}
                                </ul>
                                <p className="uc-evidence-note">
                                  The verifier reports and cites; it scores nothing. The link — and
                                  the human who confirmed it — remains the evidence of record.
                                </p>
                              </details>
                            ) : (
                              <span
                                className="uc-evidence-unverified"
                                data-testid={`uc-evidence-unverified-${item.itemId}-${test.testCode}`}
                              >
                                unverified — linked by a human, not yet read by the verifier
                              </span>
                            ))}
                          {canEdit && (
                            <button
                              type="button"
                              className="banking-btn banking-btn-secondary uc-evidence-unlink"
                              disabled={busy}
                              onClick={() => void unlink(item.linkId)}
                            >
                              Unlink
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                  {test.manualEntries.length === 0 && test.evidence.length === 0 && (
                    <p className="uc-evidence-none" data-testid={`uc-evidence-none-${test.testCode}`}>
                      Nothing answers this test yet.
                    </p>
                  )}

                  {canEdit && (
                    <div className="uc-evidence-actions">
                      {specParsed && (
                        <button
                          type="button"
                          className="banking-btn banking-btn-secondary"
                          onClick={() =>
                            setOpenForm(openForm === `metric-${test.testCode}` ? null : `metric-${test.testCode}`)
                          }
                        >
                          Supply value
                        </button>
                      )}
                      <button
                        type="button"
                        className="banking-btn banking-btn-secondary"
                        onClick={() =>
                          setOpenForm(openForm === `interview-${test.testCode}` ? null : `interview-${test.testCode}`)
                        }
                      >
                        Interview attestation
                      </button>
                      <button
                        type="button"
                        className="banking-btn banking-btn-secondary"
                        onClick={() =>
                          setOpenForm(openForm === `external-${test.testCode}` ? null : `external-${test.testCode}`)
                        }
                      >
                        External artefact
                      </button>
                      <button
                        type="button"
                        className="banking-btn banking-btn-secondary"
                        onClick={() =>
                          setOpenForm(openForm === `system-${test.testCode}` ? null : `system-${test.testCode}`)
                        }
                      >
                        System attestation
                      </button>
                    </div>
                  )}
                  {canEdit && openForm === `metric-${test.testCode}` && (
                    <MetricEntryForm useCaseId={useCaseId} test={test} onRecorded={recorded} />
                  )}
                  {canEdit && openForm === `interview-${test.testCode}` && (
                    <InterviewForm useCaseId={useCaseId} testCode={test.testCode} onRecorded={recorded} />
                  )}
                  {canEdit && openForm === `external-${test.testCode}` && (
                    <ExternalForm useCaseId={useCaseId} testCode={test.testCode} onRecorded={recorded} />
                  )}
                  {canEdit && openForm === `system-${test.testCode}` && (
                    <SystemForm useCaseId={useCaseId} testCode={test.testCode} onRecorded={recorded} />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </section>
  );
};

export default UseCaseEvidence;
